import { useReactiveVar } from '@apollo/client'
import { filterContextVar, filtersVar, pageVar } from 'graphql/reactive-variables'
import { useMemo, useEffect } from 'react'
import Filters, { Filter, FilterState, FilterChange } from '../components/Filters'

/**
 *
 * @param context e.g. what page is this used on? If this changes, then the filters will be reset
 * @param availableFilters
 * @param buildVariables
 * @param onFilterChange - called when filters change, returns { filter: string filterKey, value: boolean newValue } of the filter that was changed
 * @returns
 */
export default function useFilters( context: string, availableFilters: Filter[], buildVariables?: ( filterState: FilterState ) => any, onFilterChange?: FilterChange, filterLabel?: string ) {
	const filterState = useReactiveVar( filtersVar )
	const currentContext = useReactiveVar( filterContextVar )

	const setFilterState = ( fs: FilterState ) => {
		filtersVar( fs )
		pageVar( 0 )
	}

	useEffect( () => {
		const defaultFilters: FilterState = availableFilters.map( ( { key, label, initialValue } ) => ( {
			key,
			label,
			checked: initialValue ? 'on' : undefined,
		} ) )

		if ( filterState.length === 0 || context !== currentContext ) {
			filtersVar( defaultFilters )
		}
	}, [ availableFilters ] )

	useEffect( () => {
		filterContextVar( context )
	}, [ context ] )

	const filterVariables = useMemo<Filter>( () => {
		if ( buildVariables ) {
			return buildVariables( filterState )
		}

		const variables: { [ key: string ]: boolean } = {}
		filterState.forEach( ( filterOption ) => {
			if ( filterOption.checked === 'on' ) {
				variables[ filterOption.key ] = true
			}
		} )
		return variables
	}, [ filterState, buildVariables ] )

	const numFilters = useMemo( () => availableFilters.length, [ availableFilters ] )
	const numActiveFilters = useMemo( () => {
		let count = 0
		filterState.forEach( ( filterOption ) => {
			if ( filterOption.checked === 'on' ) {
				count++
			}
		} )
		return count
	}, [ filterState ] )

	return {
		filterState,
		setFilterState,
		filterVariables,
		render: ( isLoading?: boolean ) => (
			<Filters
				label={filterLabel}
				filterState={filterState}
				setFilterState={setFilterState}
				numFilters={numFilters}
				numActiveFilters={numActiveFilters}
				onFilterChange={onFilterChange}
				isLoading={isLoading}
			/>
		)
	}
}
